import {
  type InstanceEditableProps,
} from '../../editor/commandBuilders.js';
import type { AlignSelf, ObjectFit, Scene, SceneNodeId } from '../../scene/sceneState.js';
import {
  createNullableNumberInput,
  createNumberInput,
  createPropRow,
  createReadonlyDimensionInput,
  createSectionHeader,
  type SceneTreeWidgetDeps,
} from './widgets.js';
import {
  findParentFrameId,
  frameLayoutDirection,
  measureLayerIntrinsicSize,
} from './queries.js';
import { setInstancePropertyAndRender } from './propertyActions.js';
import {
  setInlineLayerGeometrySizeMode,
  setLayerIntrinsicSizeMode,
  type SlotInspectorDeps,
} from './slotInspector.js';
import { getRenderThemeValues } from '../../app/viewportRenderTheme.js';

type LeafPropsDeps = Pick<SlotInspectorDeps, 'appState' | 'commands' | 'scheduleRender'> & SceneTreeWidgetDeps & {
  renderSceneTree: () => void;
  selection: SlotInspectorDeps['selection'];
};

export function renderLeafProps(
  panel: HTMLElement,
  node: (InstanceEditableProps & { id: SceneNodeId; fit: ObjectFit; kind: 'instance' | 'layer'; layerId?: string }),
  activeScene: Scene,
  deps: LeafPropsDeps,
): void {
  panel.appendChild(createSectionHeader('size'));

  const appState = deps.appState;
  const layerDef = node.kind === 'layer' && node.layerId ? appState.layerLibrary[node.layerId] : null;
  const inlineGeometry = node.kind === 'layer' && layerDef?.geometry.type === 'inline'
    ? layerDef.geometry
    : null;
  const isInlineGeometry = inlineGeometry !== null;
  const currentSizeMode = inlineGeometry?.sizeMode;
  // Per-axis content flags (legacy layoutBounds reads as both-content until
  // the engine converts it on the next load).
  const axes = (() => {
    if (currentSizeMode?.kind === 'axes') {
      return { width: currentSizeMode.width === 'content', height: currentSizeMode.height === 'content' };
    }
    if (currentSizeMode?.kind === 'layoutBounds') return { width: true, height: true };
    return { width: false, height: false };
  })();
  const sizeModeChoice = axes.width && axes.height
    ? 'intrinsic'
    : axes.height ? 'fixed-width' : axes.width ? 'fixed-height' : 'explicit';
  const isIntrinsicSizeMode = node.kind === 'layer' && (axes.width || axes.height);
  const intrinsicSize = isIntrinsicSizeMode && node.kind === 'layer' && node.layerId
    ? measureLayerIntrinsicSize(deps.appState, node.layerId, getRenderThemeValues())
    : null;

  if (node.kind === 'layer') {
    const sourceRow = createPropRow('sizeMode');
    const sourceSelect = document.createElement('select');
    sourceSelect.className = 'scene-prop-input';
    const sourceOptions: Array<[string, string]> = [
      ['explicit', 'explicit'],
      ['intrinsic', 'intrinsic'],
      ['fixed-width', 'fixed width (wrap)'],
      ['fixed-height', 'fixed height'],
    ];
    for (const [val, label] of sourceOptions) {
      const opt = document.createElement('option');
      opt.value = val;
      opt.textContent = label;
      if (val === sizeModeChoice) opt.selected = true;
      sourceSelect.appendChild(opt);
    }
    if (!isInlineGeometry) sourceSelect.disabled = true;
    sourceSelect.addEventListener('change', () => {
      if (!node.layerId) return;
      const next = sourceSelect.value;
      const mode = next === 'intrinsic'
        ? { kind: 'axes', width: 'content', height: 'content' } as const
        : next === 'fixed-width'
          ? { kind: 'axes', width: 'fill', height: 'content' } as const
          : next === 'fixed-height'
            ? { kind: 'axes', width: 'content', height: 'fill' } as const
            : undefined;
      if (next === 'intrinsic') {
        if (!setLayerIntrinsicSizeMode(deps, node.layerId)) return;
      } else if (!setInlineLayerGeometrySizeMode(deps, node.layerId, mode)) return;
      deps.renderSceneTree();
    });
    sourceRow.appendChild(sourceSelect);
    panel.appendChild(sourceRow);
  }

  // Per-axis rows: a CONTENT axis shows the measured intrinsic (read-only),
  // a FILL axis stays editable — mixed modes (fixed width (wrap) / fixed
  // height) edit exactly the fixed axis.
  {
    const wRow = createPropRow('width');
    if (axes.width) {
      wRow.appendChild(createReadonlyDimensionInput(
        intrinsicSize?.w ?? 0,
        'px',
        node.kind === 'layer' && node.layerId ? `w:${node.layerId}` : undefined,
      ));
    } else {
      wRow.appendChild(createNullableNumberInput(deps, node.width, v => { setInstancePropertyAndRender(deps, node.id, 'width', v); }, 100, 'Set width'));
    }
    panel.appendChild(wRow);

    const hRow = createPropRow('height');
    if (axes.height) {
      hRow.appendChild(createReadonlyDimensionInput(
        intrinsicSize?.h ?? 0,
        'px',
        node.kind === 'layer' && node.layerId ? `h:${node.layerId}` : undefined,
      ));
    } else {
      hRow.appendChild(createNullableNumberInput(deps, node.height, v => { setInstancePropertyAndRender(deps, node.id, 'height', v); }, 100, 'Set height'));
    }
    panel.appendChild(hRow);
  }

  const selectedSceneNodeId = deps.selection.getSelectedSceneNodeId();

  const parentLayout = (() => {
    const pid = findParentFrameId(activeScene, selectedSceneNodeId!);
    const pf = pid ? activeScene.nodes[pid] : null;
    if (pf?.kind !== 'frame') return null;
    return frameLayoutDirection(deps.appState, pf.layout);
  })();

  if (parentLayout === 'row' || parentLayout === 'column' || parentLayout === 'stack') {
    panel.appendChild(createSectionHeader('child'));
  }

  if (parentLayout === 'row' || parentLayout === 'column') {
    const growRow = createPropRow('flexGrow');
    growRow.appendChild(createNumberInput(deps, node.flexGrow, 0, 100, v => {
      setInstancePropertyAndRender(deps, node.id, 'flexGrow', v);
    }, 0.1, 'Set flex-grow', { subtype: 'factor' }));
    panel.appendChild(growRow);

    const shrinkRow = createPropRow('flexShrink');
    shrinkRow.appendChild(createNumberInput(deps, node.flexShrink, 0, 100, v => {
      setInstancePropertyAndRender(deps, node.id, 'flexShrink', v);
    }, 0.1, 'Set flex-shrink', { subtype: 'factor' }));
    panel.appendChild(shrinkRow);

    const basisRow = createPropRow('flexBasis');
    basisRow.appendChild(createNullableNumberInput(deps, node.flexBasis, v => { setInstancePropertyAndRender(deps, node.id, 'flexBasis', v); }, 0, 'Set flex-basis'));
    panel.appendChild(basisRow);
  }

  if (parentLayout === 'row' || parentLayout === 'column' || parentLayout === 'stack') {
    const alignRow = createPropRow('alignSelf');
    const alignSelect = document.createElement('select');
    alignSelect.className = 'scene-prop-input';
    const alignOptions: [AlignSelf, string][] = [
      ['auto', 'auto (inherit)'],
      ['stretch', 'stretch'],
      ['flex-start', 'flex-start'],
      ['center', 'center'],
      ['flex-end', 'flex-end'],
    ];
    for (const [val, label] of alignOptions) {
      const opt = document.createElement('option');
      opt.value = val;
      opt.textContent = label;
      if (node.alignSelf === val) opt.selected = true;
      alignSelect.appendChild(opt);
    }
    alignSelect.addEventListener('change', () => {
      const next = alignSelect.value as AlignSelf;
      if (node.alignSelf === next) return;
      setInstancePropertyAndRender(deps, node.id, 'alignSelf', next);
    });
    alignRow.appendChild(alignSelect);
    panel.appendChild(alignRow);
  }

  if (node.kind === 'layer') {
    const fitRow = createPropRow('fit');
    const fitSelect = document.createElement('select');
    fitSelect.className = 'scene-prop-input';
    for (const val of ['contain', 'cover', 'fill'] as const) {
      const opt = document.createElement('option');
      opt.value = val;
      opt.textContent = val;
      if (node.fit === val) opt.selected = true;
      fitSelect.appendChild(opt);
    }
    fitSelect.addEventListener('change', () => {
      const next = fitSelect.value as ObjectFit;
      if (node.fit === next) return;
      setInstancePropertyAndRender(deps, node.id, 'fit', next);
    });
    fitRow.appendChild(fitSelect);
    panel.appendChild(fitRow);
  }
}
